import { EventNames, isServerRendering } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { FormEvent, useRef } from 'react';
import { Collapse } from 'react-collapse';

import { PurchaseConfirmationForm } from '../../../../../../../Common/Pim/Enums/PurchaseConfirmationForm.csharp';
import { addParameter, ExperimentsParamName, useExperimentsForComponent } from '../../../../../../Experimentation/Components/ResolveExperimentId';
import { OptimizelySearchTrackingInfo } from '../../../../../../Filtering/Api/OptimizelySearchTrackingInfo.csharp';
import { SearchQueryParamName, trackEpiFindClick } from '../../../../../../Filtering/Api/TrackEpiFindClick';
import { post } from '../../../../../../ReactComponents/API';
import { CooledProductsConflictEvent } from '../../../../../../ReactComponents/CooledProductsConflict/CooledProductsConflictEvent';
import { PriceDisplay } from '../../../../../../ReactComponents/PriceDisplay';
import { maxQuantity } from '../../../../../../ReactComponents/QuantityUtils';
import { GoogleTagManager } from '../../../../../../Tracking/GoogleTagManager/GoogleTagManager';
import { GoogleTagManagerList } from '../../../../../../Tracking/GoogleTagManager/GoogleTagManagerList';
import { IsFromPriceSpiderAccessor } from '../../../../../IsFromPriceSpiderAccessor';
import { handleProductCardRequest } from '../../../../Components/CartUtils';
import { ValidationDetailedInfo } from '../../../../Validation/ValidationResult/ValidationDetailedInfo.csharp';
import { CartViewModel } from '../../../../ViewModels/CartViewModel.csharp';
import { CartChangeQuantityActionResponse } from '../../../CartChangeQuantityActionResponse.csharp';
import { AddToCartMode } from '../../ViewModel/AddToCartMode.csharp';
import { AddToCartProps } from '../../ViewModel/AddToCartProps.csharp';
import { AddToCartButton } from '../AddToCartButton/AddToCartButton';
import { AddToCartForms } from '../AddToCartForms';
import { AddToCartMessages } from '../AddToCartMessages';
import { AddToCartQuantityPicker } from '../AddToCartQuantityPicker/AddToCartQuantityPicker';

interface AddToCartState {
    readonly quantity: number | '';
    errors: string[];
    isLoading: boolean;
    displaySucceededMessage: boolean;
    isFormVisible: boolean;
    maxQuantity: number;
    minQuantity: number;
    isMobileNameVisible: boolean;
    quantityPickerValue: number;
    quantityOrdered: number;
}

export const AddToCart = (props: AddToCartProps) => {
    const contentRef = useRef(null);

    const experiments = useExperimentsForComponent(contentRef);

    const resolveSearchQuery = () => {
        if (isServerRendering()) {
            return '';
        }

        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get(SearchQueryParamName) ?? '';
    };

    const searchTrackingInfo = {
        searchQuery: resolveSearchQuery(),
        hitType: props.type,
        hitId: props.id,
        trackId: ''
    } as OptimizelySearchTrackingInfo;

    return (
        <div ref={contentRef}>
            <AddToCartSection {...props} experiments={experiments} searchTrackingInfo={searchTrackingInfo} position={1} disableQuantityPicker />
        </div>
    );
};

interface AddToCartButtonProps extends AddToCartProps {
    experiments: string[];
    searchTrackingInfo: OptimizelySearchTrackingInfo;
    position: number;
    disableQuantityPicker?: boolean;
}

export class AddToCartSection extends React.Component<AddToCartButtonProps, AddToCartState> {
    private collapseNameOffset = 100;

    constructor(props: AddToCartButtonProps) {
        super(props);

        this.state = {
            quantity: 1,
            errors: [],
            isLoading: false,
            displaySucceededMessage: false,
            isFormVisible: false,
            maxQuantity: props.model.maxQuantity,
            minQuantity: props.model.minQuantity,
            isMobileNameVisible: false,
            quantityPickerValue: 0,
            quantityOrdered: 0
        };

        this.addToCart = this.addToCart.bind(this);
        this.submit = this.submit.bind(this);
    }

    componentDidUpdate(prevProps: AddToCartProps) {
        if (prevProps.model.code !== this.props.model.code) {
            this.setState({ errors: [] });
        }
    }

    componentDidMount() {
        if (this.props.isProductPage) {
            document.body.classList.add('BodyProductPage');
            window.addEventListener('scroll', this.handleShowName);
            this.handleShowName();
        }
    }

    componentWillUnmount() {
        this.props.isProductPage && window.removeEventListener('scroll', this.handleShowName);
    }

    render() {
        const {
            model: {
                warningMessage,
                isOnPrescription,
                rxMaintenance,
                mode,
                formConfiguration,
                nameInfo,
                image,
                placedPrice,
                profile,
                code,
                showRefrigeratedProductWarning
            },
            isProductPage,
            model,
            prescriptionUrl,
            isQuantityVisible,
            disableQuantityPicker
        } = this.props;
        const {
            minQuantity,
            maxQuantity,
            quantity,
            isFormVisible,
            isLoading,
            errors,
            isMobileNameVisible,
            quantityPickerValue,
            quantityOrdered,
            displaySucceededMessage
        } = this.state;

        const getMessages = (floating?: boolean) => (
            <AddToCartMessages
                errors={errors}
                warningMessage={warningMessage}
                isProductPage={isProductPage}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                closeErrorMessage={this.closeErrorMessage}
                isOnPrescription={isOnPrescription}
                rxMaintenance={!floating ? rxMaintenance : undefined}
                isNotAvailable={mode === AddToCartMode.NotAvailable}
                isOutOfStock={mode === AddToCartMode.OutOfStock}
                showRefrigeratedProductWarning={showRefrigeratedProductWarning}
            />
        );

        return (
            <>
                <form
                    onSubmit={this.submit}
                    className={classNames(
                        'AddToCart',
                        isLoading && 'AddToCart--Loading',
                        (!isQuantityVisible || mode !== AddToCartMode.Default) && 'AddToCart--WithoutQuantity'
                    )}
                >
                    {this.props.isQuantityVisible && mode === AddToCartMode.Default && (
                        <div className="AddToCart__InputNumber">
                            <button type="button" onClick={this.decreaseQuantity} disabled={(quantity as number) <= minQuantity}>
                                -
                            </button>
                            <input
                                name="quantity"
                                type="number"
                                value={quantity}
                                onChange={this.handleQuantityChange}
                                onBlur={this.handleBlur}
                                onFocus={(e) => e.target.select()}
                            />
                            <button type="button" onClick={this.increaseQuantity} disabled={(quantity as number) >= maxQuantity}>
                                +
                            </button>
                        </div>
                    )}

                    {getMessages()}

                    {isProductPage ? (
                        <div
                            className={classNames(
                                'AddToCart__ButtonWrapper',
                                (mode === AddToCartMode.Default || mode == AddToCartMode.Prescription) && 'AddToCart__ButtonWrapper--Floating'
                            )}
                        >
                            <Collapse isOpened={isMobileNameVisible}>
                                <div className="AddToCart__MobileProductNameWrapper">
                                    <span className="AddToCart__MobileProductName">
                                        {nameInfo.mainName} {nameInfo.productHeader2}
                                    </span>
                                    <span className="AddToCart__MobileProductPrice">
                                        <PriceDisplay price={placedPrice * (quantity || 1)} roundUp />
                                    </span>
                                </div>
                            </Collapse>

                            {getMessages(true)}

                            <AddToCartButton
                                model={model}
                                isLoading={isLoading}
                                isProductPage={isProductPage}
                                prescriptionUrl={prescriptionUrl}
                                displaySucceededMessage={displaySucceededMessage}
                            />
                        </div>
                    ) : (
                        <>
                            {quantityPickerValue > 0 && !disableQuantityPicker ? (
                                <AddToCartQuantityPicker
                                    minQuantity={minQuantity}
                                    maxQuantity={maxQuantity}
                                    value={quantityPickerValue}
                                    setValue={(value: number) => this.setState({ quantityPickerValue: value })}
                                    modifyQuantity={(value: number) => this.addToCart(code, value)}
                                    isLoading={isLoading}
                                    quantityOrdered={quantityOrdered}
                                />
                            ) : (
                                <AddToCartButton
                                    model={model}
                                    isLoading={isLoading}
                                    isProductPage={isProductPage}
                                    prescriptionUrl={prescriptionUrl}
                                    displaySucceededMessage={displaySucceededMessage}
                                />
                            )}
                        </>
                    )}
                </form>

                <AddToCartForms
                    formConfiguration={formConfiguration}
                    isFormVisible={isFormVisible}
                    addToCart={() => this.addToCart(code, quantity)}
                    closeForm={() => this.setState({ isFormVisible: false })}
                    productBrand={nameInfo.mainName}
                    productName={nameInfo.additionalName}
                    productPrice={placedPrice}
                    productImage={image}
                    pictureProfile={profile}
                />
            </>
        );
    }

    private handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const quantity = e.target.value;
        const parsedQuantity = parseInt(quantity);

        if (quantity === '' || (parsedQuantity > 0 && parsedQuantity <= maxQuantity)) {
            this.setState({ quantity: parsedQuantity || '' });
        }
    };

    private handleBlur = () => {
        if (!(this.state.quantity || 0 > 0)) {
            this.setState({ quantity: 1 });
        }
    };

    private submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (this.props.model.formConfiguration.type !== PurchaseConfirmationForm.Unknown) {
            return this.validateProduct(this.props.model.code, this.state.quantity);
        }

        if (!this.state.isLoading) {
            this.addToCart(this.props.model.code, this.state.quantity);
        }
    }

    private validateProduct(productCode: string, quantity: number | '') {
        const url = this.props.validateItemUrl.transformUrl({ productCode });

        this.closeErrorMessage();
        this.setState({ isLoading: true });
        handleProductCardRequest(post(url, { quantity }))
            .then(() => {
                this.setState({ isFormVisible: true, isLoading: false });
            })
            .catch((result: CartChangeQuantityActionResponse) => {
                this.setState({
                    isLoading: false,
                    maxQuantity: result?.maxQuantity || this.state.maxQuantity,
                    minQuantity: result?.minQuantity || this.state.minQuantity,
                    errors: result?.validationIssues?.map((x) => x.code)
                });
            });
    }

    private addToCart(productCode: string, quantity: number | '') {
        let url = this.props.cartItemUrl.transformUrl({ productCode });

        if (this.props.experiments?.length > 0) {
            for (const experiment of this.props.experiments) {
                url = addParameter(url, ExperimentsParamName, experiment);
            }
        }

        if (IsFromPriceSpiderAccessor.get(productCode)) {
            url = addParameter(url, 'isFromPriceSpider', 'true');
        }

        const originName = this.resolveOriginName();
        if (originName) {
            url = addParameter(url, 'originName', originName);
        }

        const { promotionId, promotionName } = this.resolvePromotionIdAndName();
        if (promotionId) {
            url = addParameter(url, 'promotionId', promotionId);
        }

        this.setState({ isLoading: true });
        handleProductCardRequest(post(url, { quantity }))
            .then((result) => {
                const event = new CustomEvent(EventNames.CartChanged, {
                    detail: { cart: result.cart, lastUpdatedItem: productCode }
                });
                window.dispatchEvent(event);

                const quantityInt = quantity || 0;

                if (quantityInt >= 0) {
                    GoogleTagManager.addToCart(this.props.model, quantityInt, result.trackingData, originName, promotionId, promotionName);
                    window.dispatchEvent(new CustomEvent(EventNames.ToggleCart, { detail: { open: true } }));
                } else {
                    const item = result.cart?.items?.find((x) => x.code === productCode);
                    if (item) {
                        window.dispatchEvent(new CustomEvent(EventNames.ToggleCart, { detail: { open: false } }));
                        GoogleTagManager.removeFromCart(item, quantityInt * -1);
                    }
                }

                if (this.props.showSucceededMessageAfterSave && quantityInt >= 0) {
                    this.setState({ isLoading: false, displaySucceededMessage: true }, () => {
                        setTimeout(() => this.setState({ displaySucceededMessage: false }), 2000);
                    });
                } else {
                    this.setState({ isLoading: false });
                }

                this.setQuantityForPicker(result.cart, productCode);

                IsFromPriceSpiderAccessor.clear(productCode);

                this.closeErrorMessage();
            })
            .catch((result: CartChangeQuantityActionResponse) => {
                const errors = result?.validationIssues?.map((x) => x.code);
                this.setState({
                    errors: errors.filter((x) => x !== ValidationDetailedInfo.CannotBeAddedDueToCooledProductsConflict),
                    maxQuantity: result?.maxQuantity || this.state.maxQuantity,
                    minQuantity: result?.minQuantity || this.state.minQuantity,
                    isLoading: false
                });

                if (result?.cart) {
                    const event = new CustomEvent(EventNames.CartChanged, {
                        detail: { cart: result?.cart, lastUpdatedItem: '' }
                    });
                    window.dispatchEvent(event);

                    this.setQuantityForPicker(result.cart, productCode);
                }

                const hasCooledProductsConflict = errors?.some((x) => x === ValidationDetailedInfo.CannotBeAddedDueToCooledProductsConflict) ?? false;
                if (hasCooledProductsConflict) {
                    window.dispatchEvent(
                        CooledProductsConflictEvent.create({
                            product: {
                                code: this.props.model.code,
                                name: this.props.model.nameInfo,
                                image: this.props.model.image,
                                isRx: false,
                                isCooled: this.props.model.isCooled
                            },
                            onThisKept: () => this.addToCart(productCode, quantity)
                        })
                    );
                }
            });

        trackEpiFindClick(this.props.epiFindClickTrackingUrl, this.props.position, this.props.searchTrackingInfo);
    }

    private setQuantityForPicker = (cart: CartViewModel, productCode: string) => {
        const item = cart?.items?.find((x) => x.code === productCode);
        if (item) {
            this.setState({ quantityPickerValue: item.quantityOrdered, quantityOrdered: item.quantityOrdered });
        }
    };

    private resolveOriginName = () => {
        const params = new URLSearchParams(window.location.search);
        // Product page will pass null to props so that origin is taken from query with fallback to product page
        // This will ensure we will track correct origin when we return cached product page
        // If addToCart is clicked on product card we will use value from props
        return this.props.originName ?? params.get('originName') ?? GoogleTagManagerList.productPage;
    };

    private resolvePromotionIdAndName = () => {
        const params = new URLSearchParams(window.location.search);

        const promotionId = this.props.promotionId ?? params.get('promotionId') ?? undefined;
        const promotionName = this.props.promotionName ?? params.get('promotionName') ?? undefined;

        return { promotionId, promotionName };
    };

    private handleShowName = () => {
        this.setState({
            isMobileNameVisible: window.scrollY > this.collapseNameOffset
        });
    };

    decreaseQuantity = () => {
        this.setState({
            quantity: !!this.state.quantity && this.state.quantity > 1 ? this.state.quantity - 1 : 1
        });
    };

    increaseQuantity = () => {
        this.setState({ quantity: this.state.quantity && this.state.quantity + 1 });
    };

    closeErrorMessage = () => {
        this.setState({ errors: [] });
    };
}
